import { useMemo } from 'react'
import { useTable, useSortBy } from 'react-table'

import Icon from '@mui/material/Icon'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { TablePagination, useMediaQuery } from '@mui/material'

const AdxSortTable = ({
  data,
  column,
  nav,
  onClick,
  divClick = false,
  sx,
  sxHeader,
  sxCell,
  sxNav,
  stickyHeader,
  allColumnSorted = true,
  pagination = false,
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  height,
  ...rest
}) => {
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  const tableHeader = useMemo(() => column, [column])
  const tableInstance = useTable(
    {
      columns: tableHeader,
      data: data,
      sortTypes: {
        alphanumeric: (prev, curr, columnId) => {
          const prevValue = prev?.original[columnId]
          const currValue = curr?.original[columnId]

          if (typeof prevValue === 'number' && typeof currValue === 'number') {
            // Compare numeric values
            return prevValue - currValue
          } else {
            // Compare string values (case-insensitive)
            const prevString = prevValue?.toString().toLowerCase() || ''
            const currString = currValue?.toString().toLowerCase() || ''
            if (prevString > currString) {
              return 1
            } else if (prevString < currString) {
              return -1
            } else {
              return 0
            }
          }
        },
      },
    },
    useSortBy
  )
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance
  return (
    <>
      <TableContainer
        className="scrollbar"
        sx={{
          bgcolor: 'white.main',
          maxHeight: height,
          boxShadow: 'none',
          '& th.MuiTableCell-root': { zIndex: 100 },
          '& td.MuiTableCell-root': {
            padding: '0px',
          },
          ...sx,
        }}
        {...rest}
      >
        <Table stickyHeader={stickyHeader} {...getTableProps}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <TableCell
                    align="center"
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: index > 0 || allColumnSorted ? 'space-between' : 'start',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'light-gray-text.main',
                          display: 'flex',
                          alignItems: 'center',
                          textAlign: 'start',
                          justifyContent: 'space-between',
                          ...sxHeader,
                        }}
                      >
                        {column.render('Header')}
                      </Typography>
                      {allColumnSorted && (
                        <>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                arrow_drop_up
                              </Icon>
                            ) : (
                              <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                arrow_drop_down
                              </Icon>
                            )
                          ) : (
                            <Icon
                              sx={{
                                fontSize: '18px',
                                color: 'gray-inactive-button.main',
                              }}
                            >
                              arrow_drop_down
                            </Icon>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                ))}
                {nav && (
                  <TableCell>
                    <Typography></Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'gray-menu-hover.main' },
                  }}
                >
                  {row.cells.map((cell, index) => {
                    return (
                      <TableCell align="center" {...cell.getCellProps()}>
                        <Box
                          onClick={divClick ? () => onClick(row.original) : () => {}}
                          sx={{
                            textAlign: index === 0 && 'left',
                            p: 2,
                            cursor: divClick ? 'pointer' : 'auto',
                            ...sxCell,
                          }}
                        >
                          {cell.render('Cell')}
                        </Box>
                      </TableCell>
                    )
                  })}
                  {nav && (
                    <TableCell align="right">
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: 'alternate.main',
                          cursor: 'pointer',
                          padding: '18px 30px',
                          ...sxNav,
                        }}
                        onClick={() => onClick(row.original)}
                      >
                        {nav}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <Box borderTop={'1px solid'} borderColor={'gray-line.main'}>
          <TablePagination
            component="div"
            labelRowsPerPage={isLarge ? 'Show' : null}
            SelectProps={{ sx: { borderRadius: 1 } }}
            showFirstButton
            showLastButton
            rowsPerPageOptions={[5, 10, 20, 50]}
            count={count}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </>
  )
}

export default AdxSortTable
